import { useEffect } from "react";

import { IPetItemResponse } from "../services/pet-list/pet-list.interface";
import {
  IPrequalificationInfoResponse,
  hasValidPrequalification,
  usePrequalificationInfo,
} from "../services/prequalification";
import useLocalStorage from "./useLocalStorage";
import { usePetStore } from "./redux-hooks/pet-store/pet-store.service";

export interface UsePrequalContinuationRecordResult {
  /** Non-null only when the continuation card should be offered. */
  record: IPrequalificationInfoResponse | null;
  pet: IPetItemResponse | null;
  dismissed: boolean;
  dismiss: () => void;
}

/**
 * Dashboard-side gate for the purchase-continuation card (SGPD-475): fetches
 * the latest valid prequalification and yields it only while the purchase is
 * still outstanding (record valid, pet resolvable and not yet enrolled).
 *
 * TODO(SGPD-475): dismissal is client-local (localStorage keyed by the
 * prequalification record id). Open question with BE whether a server-side
 * dismissed flag (a la dismissPrequalificationBanner) should replace this.
 *
 * @return {UsePrequalContinuationRecordResult} Card gating state + dismiss.
 */
export const usePrequalContinuationRecord = (): UsePrequalContinuationRecordResult => {
  const { petList } = usePetStore();
  const { fetchPrequalificationInfo, prequalificationInfo } = usePrequalificationInfo();
  const [dismissedIds, setDismissedIds] = useLocalStorage<string[]>(
    "prequalContinuationDismissedIds",
    [],
  );

  useEffect(() => {
    fetchPrequalificationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Valid = ELIGIBLE/INELIGIBLE, not expired, not yet fulfilled (shared with
  // the renewal gate via hasValidPrequalification).
  const isValidRecord = hasValidPrequalification(prequalificationInfo);
  const pet = isValidRecord ?
    petList?.find((petItem) => petItem.id === prequalificationInfo?.petId) ?? null :
    null;
  // Still outstanding (continuation card should show) when the record is valid
  // and its pet is resolvable and not yet enrolled in a wellness plan.
  const isOutstanding = Boolean(isValidRecord && pet && !pet.isWellnessplan);
  const dismissed = Boolean(
    prequalificationInfo?.id && dismissedIds.includes(prequalificationInfo.id),
  );

  const dismiss = () => {
    if (!prequalificationInfo?.id) return;
    const recordId = prequalificationInfo.id;
    setDismissedIds((ids) => (ids.includes(recordId) ? ids : [...ids, recordId]));
  };

  return {
    record: isOutstanding ? prequalificationInfo ?? null : null,
    pet: isOutstanding ? pet : null,
    dismissed,
    dismiss,
  };
};
